import {
  Markup,
  Tax,
  MarkupAmountType,
  TaxAmountType,
} from 'lane-shared/domains/billingPayments/types';
import { getCurrencyPrecision, type SupportedPaymentCurrency } from 'domains-billing-currency';
import { formatCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';

import { PriceComponents } from '../types';
import BigNumber from 'bignumber.js';

export type PricingParams = {
  currency: SupportedPaymentCurrency;
  locale: SupportedLocaleEnum;
  priceComponents: PriceComponents;
  inclusivePricing?: boolean;
};

export type Pricing = {
  rate: number; // Base amount
  grossMarkup: number; // Markup amount in unit currency
  grossTax: number; // Tax amount in unit currency
  listPrice: number; // Sum of rate and markup amounts in unit currency
  netPrice: number; // Price after applying tax to listPrice in unit currency
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string; // Formatter function that converts the price to a string
};

export function calculateExclusiveOptionalRate(
  rate: BigNumber,
  optRate: Markup | Tax
) {
  const { value } = optRate;

  const isPercentage =
    'markupAmountType' in optRate
      ? optRate.markupAmountType === MarkupAmountType.MarkupAmountTypePercentage
      : optRate.taxAmountType === TaxAmountType.TaxAmountTypePercentage;

  const calculatedValueForPercentage = Number(
    rate.multipliedBy(value).dividedBy(100).toFixed(2)
  );

  return isPercentage ? calculatedValueForPercentage || 0 : value || 0;
}

export function calculateInclusiveOptionalRate(
  price: BigNumber,
  rate: BigNumber
) {
  return price.minus(rate).toNumber() || 0;
}

export function calculateBaseRateFromInclusiveAmount(
  price: BigNumber,
  optRate: Markup | Tax
) {
  const { value } = optRate;

  const isPercentage =
    'markupAmountType' in optRate
      ? optRate.markupAmountType === MarkupAmountType.MarkupAmountTypePercentage
      : optRate.taxAmountType === TaxAmountType.TaxAmountTypePercentage;

  return isPercentage
    ? price.dividedBy(1 + value / 100).toNumber() || 0
    : price.minus(value).toNumber() || 0;
}

export function getPricing({
  currency,
  locale,
  priceComponents,
  inclusivePricing = false,
}: PricingParams): Pricing {
  const { sellingPrice, amount } = priceComponents;

  const precision = getCurrencyPrecision(currency);

  const formatPrice = (
    amount: number,
    currencyDisplay: 'narrowSymbol' | 'none' = 'none'
  ) =>
    formatCurrency(currency, {
      locale,
      currencyDisplay,
      useGrouping: false,
    })(amount);

  let rate = 0;
  let grossMarkup = 0;
  let grossTax = 0;
  let listPrice = 0;

  if (
    sellingPrice?.listPrice !== undefined &&
    sellingPrice.grossTax !== undefined &&
    sellingPrice.netPrice !== undefined &&
    sellingPrice.grossMarkup !== undefined
  ) {
    listPrice = Number(formatPrice(sellingPrice.listPrice));
    grossTax = Number(formatPrice(sellingPrice.grossTax));
    rate = Number(formatPrice(amount));
    grossMarkup = Number(formatPrice(sellingPrice.grossMarkup));
  } else if (sellingPrice?.listPrice !== undefined) {
    ({ listPrice, rate, grossMarkup, grossTax } = calculateListPriceOverridePricing(
      priceComponents,
      formatPrice,
      precision
    ));
  } else if (inclusivePricing) {
    ({ listPrice, grossTax, rate, grossMarkup } = calculateInclusivePricing(
      priceComponents,
      formatPrice,
      precision
    ));
  } else {
    ({ listPrice, grossTax, rate, grossMarkup } = calculateExclusivePricing(
      priceComponents,
      formatPrice,
      precision
    ));
  }

  return {
    rate,
    grossMarkup,
    grossTax,
    listPrice,
    netPrice: new BigNumber(listPrice).plus(grossTax).toNumber(),
    formatPrice,
  };
}

function calculateListPriceOverridePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { sellingPrice, amount } = priceComponents;

  const listPrice = Number(formatPrice(sellingPrice!.listPrice || 0));
  const rate = Number(formatPrice(amount));
  const grossMarkup = Number(formatPrice(sellingPrice!.grossMarkup || 0));
  const grossTax = new BigNumber(
    priceComponents.tax
      ? Number(
        formatPrice(
          calculateExclusiveOptionalRate(new BigNumber(listPrice), priceComponents.tax)
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    rate,
    grossMarkup,
    grossTax,
  };
}

function calculateInclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, netPrice } = priceComponents;

  const listPrice = new BigNumber(
    tax
      ? Number(
        formatPrice(
          calculateBaseRateFromInclusiveAmount(
            new BigNumber(netPrice ?? 0),
            tax
          )
        )
      )
      : (netPrice ?? 0)
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossTax = new BigNumber(
    tax
      ? Number(
        formatPrice(
          calculateInclusiveOptionalRate(
            new BigNumber(netPrice ?? 0),
            new BigNumber(listPrice)
          )
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const rate = new BigNumber(
    markup
      ? Number(
        formatPrice(
          calculateBaseRateFromInclusiveAmount(new BigNumber(listPrice), markup)
        )
      )
      : listPrice
  )
    .decimalPlaces(precision)
    .toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
        formatPrice(
          calculateInclusiveOptionalRate(
            new BigNumber(listPrice),
            new BigNumber(rate)
          )
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
  };
}

function calculateExclusivePricing(
  priceComponents: PriceComponents,
  formatPrice: (
    amount: number,
    currencyDisplay?: 'narrowSymbol' | 'none'
  ) => string,
  precision: number
) {
  const { markup, tax, amount } = priceComponents;

  const rate = new BigNumber(amount || 0)
    .decimalPlaces(precision)
    .toNumber();

  const grossMarkup = new BigNumber(
    markup
      ? Number(
        formatPrice(
          calculateExclusiveOptionalRate(new BigNumber(amount), markup)
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  const listPrice = new BigNumber(amount)
    .plus(grossMarkup)
    .decimalPlaces(precision)
    .toNumber() || 0;

  const grossTax = new BigNumber(
    tax
      ? Number(
        formatPrice(
          calculateExclusiveOptionalRate(new BigNumber(listPrice), tax)
        )
      )
      : 0
  )
    .decimalPlaces(precision)
    .toNumber();

  return {
    listPrice,
    grossTax,
    rate,
    grossMarkup,
  };
}

